import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SECTIONS = [
  {
    title: "Programme details",
    subtitle: "Shown in the Programme card in the middle of the homepage.",
    fields: [
      { name: "cohort_label", label: "Cohort label", hint: "Shown as the sub-heading, e.g. \"Term 4, 2026 cohort\"" },
      { name: "duration", label: "Duration" },
      { name: "delivery", label: "Delivery" },
      { name: "start_label", label: "Start" },
      { name: "eligibility", label: "Who can enrol", type: "textarea" },
    ],
  },
  {
    title: "Hero stats",
    subtitle: "The three little cards under the tagline (top of homepage).",
    fields: [
      { name: "stat1_number", label: "Stat 1 · number/label", hint: "Big text (e.g. \"Level 4\")" },
      { name: "stat1_label", label: "Stat 1 · description" },
      { name: "stat2_number", label: "Stat 2 · number/label" },
      { name: "stat2_label", label: "Stat 2 · description" },
      { name: "stat3_number", label: "Stat 3 · number/label" },
      { name: "stat3_label", label: "Stat 3 · description" },
    ],
  },
  {
    title: "About Padma",
    subtitle: "The About Padma section on the homepage.",
    fields: [
      { name: "padma_role", label: "Role / title", hint: "Shown under Padma's name in the photo card" },
      {
        name: "padma_bio",
        label: "Full bio",
        type: "textarea",
        rows: 14,
        hint: "Leave a blank line between paragraphs. The last paragraph will be highlighted in green.",
      },
    ],
  },
  {
    title: "Contact details",
    subtitle: "Shown in the footer and used for the LinkedIn button.",
    fields: [
      { name: "contact_email", label: "Email" },
      { name: "contact_phone", label: "Phone (displayed)" },
      { name: "contact_phone_href", label: "Phone (dialable, no spaces)", hint: "e.g. +64211697833" },
      { name: "contact_linkedin", label: "LinkedIn URL" },
      { name: "contact_location", label: "Location line" },
    ],
  },
];

export default function ContentEditor() {
  const qc = useQueryClient();
  const contentQuery = useQuery({
    queryKey: ["site-content-admin"],
    queryFn: () => api.get("/content").then((r) => r.data),
  });
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (contentQuery.data && !form) setForm(contentQuery.data);
  }, [contentQuery.data, form]);

  const saveMut = useMutation({
    mutationFn: (data) => api.put("/admin/content", data).then((r) => r.data),
    onSuccess: (data) => {
      setForm(data);
      qc.invalidateQueries({ queryKey: ["site-content"] });
      qc.invalidateQueries({ queryKey: ["site-content-admin"] });
      setStatus({ ok: true, msg: "Saved! Your website has been updated." });
      setTimeout(() => setStatus(null), 4000);
    },
    onError: (err) => {
      setStatus({ ok: false, msg: formatApiError(err.response?.data?.detail) || err.message });
    },
  });

  if (!form) {
    return <div className="card text-[var(--slate)]">Loading content...</div>;
  }

  const setField = (name, value) => setForm({ ...form, [name]: value });

  const reset = () => {
    if (window.confirm("Reset all fields back to the currently saved version?")) {
      setForm(contentQuery.data);
      setStatus(null);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    saveMut.mutate(form);
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="content-editor-form">
      <div className="card !p-5 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10 bg-white">
        <div>
          <div className="font-display font-semibold text-lg">Edit your website content</div>
          <div className="text-sm text-[var(--slate)]">
            Changes appear on your website as soon as you click Save.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="btn btn-secondary !py-2 !px-4"
            data-testid="content-reset-btn"
          >
            <RotateCcw size={15} /> Reset
          </button>
          <button
            type="submit"
            disabled={saveMut.isPending}
            className="btn btn-primary !py-2 !px-4"
            data-testid="content-save-btn"
          >
            <Save size={16} /> {saveMut.isPending ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      {status && (
        <div
          className={`text-sm rounded-md px-4 py-3 ${
            status.ok ? "bg-[var(--sage)] text-[var(--forest)]" : "bg-red-50 text-red-700"
          }`}
          data-testid="content-status"
        >
          {status.msg}
        </div>
      )}

      {SECTIONS.map((section) => (
        <div key={section.title} className="card" data-testid={`content-section-${section.title.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="overline">{section.title}</div>
          {section.subtitle && (
            <div className="text-sm text-[var(--slate)] mt-1">{section.subtitle}</div>
          )}
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            {section.fields.map((f) => (
              <div
                key={f.name}
                className={`field ${f.type === "textarea" ? "sm:col-span-2" : ""}`}
              >
                <label htmlFor={`f-${f.name}`}>{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea
                    id={`f-${f.name}`}
                    rows={f.rows || 3}
                    value={form[f.name] || ""}
                    onChange={(e) => setField(f.name, e.target.value)}
                    data-testid={`content-field-${f.name}`}
                  />
                ) : (
                  <input
                    id={`f-${f.name}`}
                    value={form[f.name] || ""}
                    onChange={(e) => setField(f.name, e.target.value)}
                    data-testid={`content-field-${f.name}`}
                  />
                )}
                {f.hint && <div className="text-xs text-[var(--slate)] mt-1">{f.hint}</div>}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-end pt-2 pb-8">
        <button
          type="submit"
          disabled={saveMut.isPending}
          className="btn btn-primary !py-2 !px-6"
          data-testid="content-save-btn-bottom"
        >
          <Save size={16} /> {saveMut.isPending ? "Saving..." : "Save changes"}
        </button>
      </div>
    </form>
  );
}
